import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Newspaper, Tv, Radio, HeartHandshake, Plus, Trash2, Loader2, RefreshCw,
  CheckCircle2, AlertTriangle, Rss, Search, MapPin, PowerOff, Power,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const KIND_ICON = { paper: Newspaper, tv: Tv, radio: Radio, nonprofit: HeartHandshake };

export default function LocalPress() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasRole("admin", "manager");

  const { data, isLoading } = useQuery({
    queryKey: ["pressFeeds"],
    queryFn: () => api.get("/press/feeds").then((r) => r.data),
  });

  const poll = useMutation({
    mutationFn: () => api.post("/listening/poll").then((r) => r.data),
    onSuccess: (d) => {
      const lp = d.local_press || {};
      toast.success(
        `${d.new_items} new stor${d.new_items === 1 ? "y" : "ies"} · ` +
        `${lp.ok || 0} outlets fresh, ${lp.not_modified || 0} unchanged, ${lp.failed || 0} failed`
      );
      qc.invalidateQueries({ queryKey: ["pressFeeds"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const t = data?.totals;

  return (
    <div data-testid="local-press-page">
      <PageHeader
        title="Local Press Reach"
        subtitle="Hometown papers, TV and public radio from every corner of Michigan, polled politely with conditional requests, gated by your watchlist, and clustered so a wire story shared by eight outlets stays one item."
        icon={Newspaper}
        actions={
          canEdit && (
            <div className="flex flex-wrap items-center gap-3">
              <AddFeedDialog regions={data?.available_regions || []} kinds={data?.kinds || {}} />
              <Button
                data-testid="press-poll-btn"
                onClick={() => poll.mutate()}
                disabled={poll.isPending}
                className="bg-brand-red text-white hover:bg-brand-redhover"
              >
                {poll.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Sweep the press
              </Button>
            </div>
          )
        }
      />

      {isLoading ? (
        <Loader label="Loading the Michigan press catalog…" />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Stat label="Outlets on watch" value={t?.enabled} sub={`of ${t?.outlets} in catalog`} testId="press-stat-enabled" accent />
            <Stat label="Regions covered" value={`${t?.regions_covered} / 10`} sub="Michigan campaign regions" testId="press-stat-regions" />
            <Stat label="Direct RSS" value={t?.direct_rss} sub="conditional GET, near-zero cost" testId="press-stat-rss" />
            <Stat label="Via Google News" value={t?.google_news} sub="paywalled / no-RSS outlets" testId="press-stat-gnews" />
            <Stat label="Quarantined" value={t?.quarantined} sub="repeated failures" testId="press-stat-quarantined" warn={t?.quarantined > 0} />
          </div>

          {data?.terms?.length > 0 && (
            <p className="text-xs text-brand-mute" data-testid="press-terms">
              Paywalled outlets are searched for:{" "}
              {data.terms.map((x) => (
                <span key={x} className="mr-1.5 rounded bg-slate-100 px-1.5 py-0.5 font-medium text-brand-navy">{x}</span>
              ))}
, edit these in the Feed page Watchlist.
            </p>
          )}

          {(data?.regions || []).map((region) => (
            <RegionBlock key={region.name} region={region} canEdit={canEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, testId, accent, warn }) {
  return (
    <div
      className={`rounded-xl border bg-white p-4 shadow-sm ${
        warn ? "border-brand-red/40" : "border-border"
      }`}
      data-testid={testId}
    >
      <p className={`font-heading text-2xl font-extrabold tracking-tight ${accent ? "text-brand-red" : warn ? "text-brand-red" : "text-brand-ink"}`}>
        {value ?? "-"}
      </p>
      <p className="mt-0.5 text-xs font-semibold text-brand-ink">{label}</p>
      <p className="text-[0.7rem] text-brand-mute">{sub}</p>
    </div>
  );
}

function RegionBlock({ region, canEdit }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const on = region.feeds.filter((f) => f.enabled).length;

  const toggleAll = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/press/region/${encodeURIComponent(region.name)}`, null, {
        params: { enabled },
      });
      toast.success(`${region.name}: ${data.updated} outlet${data.updated === 1 ? "" : "s"} ${enabled ? "on" : "off"}`);
      qc.invalidateQueries({ queryKey: ["pressFeeds"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-white shadow-sm" data-testid={`press-region-${region.name}`}>
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
        <MapPin className="h-4 w-4 text-brand-red" />
        <div className="min-w-0">
          <h3 className="font-heading text-base font-bold text-brand-ink">{region.name}</h3>
          <p className="text-xs text-brand-mute">{region.blurb}</p>
        </div>
        <Badge variant="outline" className="ml-auto border-brand-navy/20 bg-brand-navy/[0.04] text-brand-navy">
          {on} / {region.feeds.length} on
        </Badge>
        {canEdit && (
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" disabled={busy} onClick={() => toggleAll(true)} data-testid={`press-region-all-on-${region.name}`}>
              <Power className="mr-1.5 h-3.5 w-3.5" /> All on
            </Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => toggleAll(false)} data-testid={`press-region-all-off-${region.name}`}>
              <PowerOff className="mr-1.5 h-3.5 w-3.5" /> All off
            </Button>
          </div>
        )}
      </div>
      <div className="divide-y divide-border">
        {region.feeds.map((f) => (
          <FeedRow key={f.id} feed={f} canEdit={canEdit} />
        ))}
      </div>
    </div>
  );
}

function FeedRow({ feed, canEdit }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState(null);
  const Icon = KIND_ICON[feed.kind] || Newspaper;

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/press/feeds/${feed.id}`, body);
      qc.invalidateQueries({ queryKey: ["pressFeeds"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    setProbe(null);
    try {
      const { data } = await api.post(`/press/feeds/${feed.id}/test`);
      setProbe(data);
      data.ok
        ? toast.success(`${feed.name}: ${data.items} stories`)
        : toast.error(`${feed.name}: ${data.status}`);
      qc.invalidateQueries({ queryKey: ["pressFeeds"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/press/feeds/${feed.id}`);
      toast.success(`${feed.name} removed`);
      qc.invalidateQueries({ queryKey: ["pressFeeds"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 px-5 py-3 transition-colors duration-150 hover:bg-slate-50/70" data-testid={`press-feed-${feed.slug}`}>
      <Icon className={`h-4 w-4 shrink-0 ${feed.enabled ? "text-brand-navy" : "text-slate-300"}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-sm font-semibold ${feed.enabled ? "text-brand-ink" : "text-brand-mute"}`}>
            {feed.name}
          </span>
          <span className="text-[0.65rem] font-bold uppercase tracking-wide text-brand-mute">{feed.kind_label}</span>
          {feed.mode === "rss" ? (
            <Badge variant="outline" className="gap-1 border-brand-navy/15 text-[0.65rem] text-brand-navy"><Rss className="h-2.5 w-2.5" /> RSS</Badge>
          ) : (
            <Badge variant="outline" className="gap-1 border-brand-blue/25 text-[0.65rem] text-brand-blue"><Search className="h-2.5 w-2.5" /> Google News</Badge>
          )}
          {feed.custom && <Badge variant="outline" className="text-[0.65rem]">Yours</Badge>}
          {feed.quarantined && (
            <span className="inline-flex items-center gap-1 rounded-full bg-neg-bg px-2 py-0.5 text-[0.6rem] font-bold uppercase text-neg-text" data-testid={`press-quarantined-${feed.slug}`}>
              <AlertTriangle className="h-2.5 w-2.5" /> Quarantined
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[0.7rem] text-brand-mute">
          {feed.last_status === "not_modified" ? (
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-pos-text" /> unchanged since last check · {timeAgo(feed.last_checked_at || feed.last_ok_at)}</span>
          ) : feed.last_status === "ok" ? (
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-pos-text" /> {feed.last_items} stories · {timeAgo(feed.last_ok_at)}</span>
          ) : feed.last_status === "empty" ? (
            <span className="text-brand-mute">no stories in this feed right now · {timeAgo(feed.last_checked_at)}</span>
          ) : feed.last_status ? (
            <span className="text-brand-red" data-testid={`press-fail-${feed.slug}`}>
              last attempt: {feed.last_status}
              {feed.last_ok_at && <span className="text-brand-mute"> · last good {timeAgo(feed.last_ok_at)}</span>}
            </span>
          ) : (
            "not polled yet"
          )}
          {feed.fail_count > 0 && !feed.quarantined && <span className="text-brand-red"> · {feed.fail_count} recent failure{feed.fail_count === 1 ? "" : "s"}</span>}
          {probe?.sample?.length > 0 && <span className="block truncate text-brand-navy">“{probe.sample[0]}”</span>}
        </p>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          {feed.quarantined && (
            <Button variant="outline" size="sm" onClick={() => patch({ quarantined: false })} disabled={busy} data-testid={`press-resume-${feed.slug}`}>
              Resume
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={test} disabled={busy} data-testid={`press-test-${feed.slug}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Test"}
          </Button>
          {feed.custom && (
            <Button variant="ghost" size="sm" onClick={remove} disabled={busy} data-testid={`press-delete-${feed.slug}`}>
              <Trash2 className="h-3.5 w-3.5 text-brand-red" />
            </Button>
          )}
          <Switch
            checked={!!feed.enabled && !feed.quarantined}
            disabled={busy}
            onCheckedChange={(v) => patch({ enabled: v })}
            data-testid={`press-toggle-${feed.slug}`}
          />
        </div>
      )}
    </div>
  );
}

function AddFeedDialog({ regions, kinds }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", region: "Statewide", kind: "paper", url: "", site: "" });

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Give the outlet a name");
    if (!form.url.trim() && !form.site.trim()) return toast.error("Add an RSS URL or a website domain");
    setBusy(true);
    try {
      await api.post("/press/feeds", form);
      toast.success(`${form.name} added: hit "Test" to confirm it's live`);
      setOpen(false);
      setForm({ name: "", region: "Statewide", kind: "paper", url: "", site: "" });
      qc.invalidateQueries({ queryKey: ["pressFeeds"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="press-add-btn">
          <Plus className="mr-2 h-4 w-4" /> Add an outlet
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="press-add-dialog">
        <DialogHeader><DialogTitle>Add a hometown outlet</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="pf-name">Outlet name</Label>
            <Input id="pf-name" className="mt-1.5" placeholder="e.g. The Cheboygan Daily Tribune" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="press-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Region</Label>
              <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                <SelectTrigger className="mt-1.5" data-testid="press-region-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1.5" data-testid="press-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(kinds).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="pf-url">RSS URL <span className="text-brand-mute">(if they publish one)</span></Label>
            <Input id="pf-url" className="mt-1.5" placeholder="https://example.com/feed/" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="press-url-input" />
          </div>
          <div>
            <Label htmlFor="pf-site">…or just their domain</Label>
            <Input id="pf-site" className="mt-1.5" placeholder="example.com" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} data-testid="press-site-input" />
            <p className="mt-1 text-[0.7rem] text-brand-mute">
              No RSS? Drop the domain and we'll pull their coverage through a Google News site search
              scoped to your watchlist terms.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="press-save-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add outlet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
